import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { mechanicsContent } from '../content/mechanics';
import Overview from '../content/mechanics/Overview';
import { useStrings } from '../locales';
import './Mechanics.css';

const SECTION_ORDER: string[] = Object.keys(mechanicsContent);

export default function Mechanics() {
  const s = useStrings().mechanics;
  const ALL_TABS = ['OVERVIEW', ...SECTION_ORDER];
  const [searchParams, setSearchParams] = useSearchParams();
  const { hash } = useLocation();
  const [activeTab, setActiveTab] = useState<string>(() => {
    const raw = (searchParams.get('tab') ?? '').toUpperCase();
    return ALL_TABS.includes(raw) ? raw : 'OVERVIEW';
  });

  // Sync activeTab when ?tab= changes (handles navigating here from search while already on this page)
  useEffect(() => {
    const raw = (searchParams.get('tab') ?? '').toUpperCase();
    const tab = ALL_TABS.includes(raw) ? raw : 'OVERVIEW';
    setActiveTab(tab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // After activeTab re-renders, scroll to hash — runs after DOM update so element is guaranteed present
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const target = document.getElementById(id);
    const container = document.querySelector('.content-area') as HTMLElement | null;
    if (target && container) {
      const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollTop += offset - 24;
    }
  }, [activeTab, hash]);

  const label = (key: string) =>
    key === 'OVERVIEW' ? s.overview : key.charAt(0) + key.slice(1).toLowerCase();

  const currentIdx = ALL_TABS.indexOf(activeTab);
  const prevTab = currentIdx > 0 ? ALL_TABS[currentIdx - 1] : null;
  const nextTab = currentIdx < ALL_TABS.length - 1 ? ALL_TABS[currentIdx + 1] : null;

  const goTo = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
    document.querySelector('.content-area')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      <div className="mechanics-layout">
        {/* Sidebar, desktop */}
        <nav className="mechanics-toc">
          <h4>{s.contents}</h4>
          {ALL_TABS.map((key) => (
            <button
              key={key}
              className={`toc-link${activeTab === key ? ' active' : ''}`}
              onClick={() => goTo(key)}
            >
              {label(key)}
            </button>
          ))}
        </nav>

        <div className="mechanics-content">
          {/* Dropdown selector, mobile only */}
          <div className="mechanics-mobile-nav">
            <select
              className="mechanics-select"
              value={activeTab}
              onChange={(e) => goTo(e.target.value)}
              aria-label="Select section"
            >
              {ALL_TABS.map((key) => (
                <option key={key} value={key}>{label(key)}</option>
              ))}
            </select>
            <span className="mechanics-select-count">
              {currentIdx + 1} / {ALL_TABS.length}
            </span>
          </div>

          {/* Horizontal tab bar, desktop only */}
          <div className="mechanics-tab-bar">
            {ALL_TABS.map((key) => (
              <button
                key={key}
                className={`mechanics-tab${activeTab === key ? ' active' : ''}`}
                onClick={() => goTo(key)}
              >
                {label(key)}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 'OVERVIEW'
            ? <div className="rule-section"><h2>{s.overview}</h2><Overview /></div>
            : (() => { const Content = mechanicsContent[activeTab]; return Content ? <div className="rule-section"><h2>{label(activeTab)}</h2><Content /></div> : null; })()
          }

          {/* Prev / Next navigation — visible on mobile only */}
          <div className="mechanics-section-nav">
            {prevTab
              ? <button className="mechanics-nav-btn mechanics-nav-btn--prev" onClick={() => goTo(prevTab)}>
                  <ChevronLeft size={20} className="mechanics-nav-btn__arrow" />
                  <span className="mechanics-nav-btn__label">{label(prevTab)}</span>
                </button>
              : <span />
            }
            {nextTab
              ? <button className="mechanics-nav-btn mechanics-nav-btn--next" onClick={() => goTo(nextTab)}>
                  <span className="mechanics-nav-btn__label">{label(nextTab)}</span>
                  <ChevronRight size={20} className="mechanics-nav-btn__arrow" />
                </button>
              : <span />
            }
          </div>
        </div>
      </div>
    </div>
  );
}
